import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { PublicShell } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

export default function Login() {
  const { login, formatApiError, env } = useAuth();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [form, setForm] = useState({ identifier: "", password: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const user = await login(form.identifier.trim(), form.password);
      const next = sp.get("next");
      const target = next || (user.role === "admin" ? "/admin" : "/dashboard");
      toast.success(`Welcome back, ${user.full_name}`);
      navigate(target);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <PublicShell>
      <section className="max-w-md mx-auto px-5 sm:px-8 py-16">
        <p className="text-xs tracking-[0.22em] uppercase font-bold text-[#00C4B4]">Sign in</p>
        <h1 className="font-display mt-2 text-4xl tracking-tight font-bold text-[#0A2540]">Welcome back.</h1>
        <p className="mt-3 text-[#666666]">Use your email or South African cellphone to log in.</p>

        <form onSubmit={submit} data-testid="login-form" className="mt-8 space-y-5 bg-white border border-[#E5E7EB] rounded-2xl p-7">
          <div className="space-y-2">
            <Label htmlFor="ident">Email or Cellphone</Label>
            <Input
              data-testid="login-identifier-input"
              id="ident"
              autoFocus
              required
              placeholder="you@example.com or 082 123 4567"
              value={form.identifier}
              onChange={(e) => setForm({ ...form, identifier: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="pw">Password</Label>
            <Input
              data-testid="login-password-input"
              id="pw"
              type="password"
              required
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
            />
          </div>
          <Button
            data-testid="login-submit-btn"
            type="submit"
            disabled={busy}
            className="w-full bg-[#0A2540] hover:bg-[#133D6B] text-white"
          >
            {busy ? "Signing in…" : "Sign in"}
          </Button>
          <p className="text-sm text-[#666666] text-center">
            New to FlexiVouch?{" "}
            <Link to="/signup" data-testid="login-signup-link" className="text-[#00C4B4] font-semibold hover:underline">
              Create a merchant account
            </Link>
          </p>
        </form>
        {env?.enable_dev_otp && (
          <div className="mt-6 bg-[#F8F9FA] border border-[#E5E7EB] rounded-xl p-4 text-xs text-[#666666]" data-testid="qa-credentials-hint">
            <p className="font-semibold text-[#0A2540] mb-1">QA test accounts ({env?.environment || "non-production"})</p>
            <p>Admin: admin@flexivouch.co.za / FlexiAdmin@2026</p>
            <p>Merchant: merchant@flexivouch.co.za / MerchantTest@2026</p>
            <p className="mt-2 text-[10px] text-[#999999]">Hidden automatically in production (ENABLE_DEV_OTP=false).</p>
          </div>
        )}
      </section>
    </PublicShell>
  );
}
